import NiceModal from '@ebay/nice-modal-react';

import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import PageHeader from 'components/global/PageHeader';
import Popper from 'components/global/Popper';
import { TabTableContainer } from 'components/layout/AppLayout/AppLayout.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';
import useRouterFilters from 'hooks/useRouterFilters';

import ImportDataModal from 'modals/ImportDataModal';

import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import { ENUMS, OTOFLEX } from 'services/consts';

import { setListMapView } from 'stores/layoutSlice';

import { Breakpoints } from 'utils/const.styles';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS from 'utils/paths';

import { faDownload, faList, faLocationDot } from '@fortawesome/pro-solid-svg-icons';

import DownloadConfig from '../DownloadConfig';

const DriverList = () => {
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);
	const listMapView = useSelector((state) => state.layout.listMapView);
	const dispatch = useDispatch();
	const { showModal } = useModalRouter();
	const { pathname } = useLocation();
	const defaultSort = useMemo(() => [{ id: 'ID', desc: true }], []);
	const [buttonRef, setButtonRef] = useState(null);
	const columns = useMemo(
		() => [
			{ Header: 'driverID', accessor: 'driverID', filterType: FilterType.number, width: 50 },
			{ Header: 'userID', accessor: 'ID', filterType: FilterType.number, width: 50 },
			{
				Header: 'driverName',
				accessor: 'fullName',
				filterType: FilterType.string,
				showOnMobile: true,
				showOnMap: true
			},
			{ Header: 'email', accessor: 'email', filterType: FilterType.string },
			{
				Header: 'phone',
				accessor: 'mobileNumber',
				filterType: FilterType.string,
				renderCell: ListComponents.PHONE,
				width: 100,
				showOnMap: true
			},
			{
				Header: 'balance',
				accessor: 'cashBalance',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY,
				showOnMap: true
			},
			{
				Header: 'capacity',
				accessor: 'capacity',
				filterType: FilterType.number,
				width: 30,
				showOnMap: true
			},
			{
				Header: 'status',
				accessor: 'status',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.STATUS,
				enumName: ENUMS.USER_STATUSES,
				showOnMobile: true,
				width: 110
			},
			{
				Header: 'onDuty',
				accessor: 'onDuty',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DRIVER_DUTY,
				renderCell: ListComponents.STATUS,
				width: 110,
				showOnMap: true
			},
			{
				Header: 'activeOrders',
				accessor: 'activeOrders',
				filterType: FilterType.number,
				width: 30,
				showOnMap: true
			}
		],
		[]
	);

	const filtersFromRouter = useRouterFilters(columns);

	return (
		<TabTableContainer>
			<PageHeader
				title="otoFlex-drivers"
				lordIcon="https://storage.googleapis.com/tryoto-public/lottie-files-compressed/OTO-Flex-Driver-new.json.gz">
				<Button
					ref={setButtonRef}
					icon={faDownload}
					size="small"
					variant="outline"
					label={isMobile ? 'downloadAppMobile' : 'downloadApp'}
				/>
				{buttonRef && (
					<Popper
						wrapperId="downloadPopper"
						refElement={buttonRef}
						placement="bottom-end"
						offsetX={0}
						offsetY={1}
						hoverMode>
						{(closePopper) => <DownloadConfig closePopper={closePopper} />}
					</Popper>
				)}

				{pathname === PATHS.DRIVERS && (
					<>
						<Button
							icon={listMapView ? faList : faLocationDot}
							label={listMapView ? 'listView' : 'Mapview'}
							onClick={() => dispatch(setListMapView(!listMapView))}
							variant="borderless"
							className="mobile-hidden"
						/>
						<Authorization path="/import-export/button/driverImport">
							<Button
								variant="borderless"
								label="driverImport"
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon/outline/importData-icon.json"
								onClick={() => NiceModal.show(ImportDataModal, importActions.driverImport)}
								className="mobile-hidden importDataButton"
							/>
						</Authorization>
						<Authorization path="/modal/driver-form">
							<Button
								variant="blue"
								label="addDriver"
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon/outline/addOrder-icon.json"
								onClick={() => showModal('driver-form')}
								className="addButton"
							/>
						</Authorization>
					</>
				)}
			</PageHeader>
			<DynamicList
				id={DYNAMICLISTS.OTO_FLEX_DRIVERS}
				columns={columns}
				fetchUrl={OTOFLEX.GET_DRIVER_LIST}
				defaultFilters={filtersFromRouter}
				defaultSort={defaultSort}
				actionObjectType={ACTION_OBJECT_TYPES.OTOFLEX}
				exportName="drivers"
				showMapViewButton
			/>
		</TabTableContainer>
	);
};

export default DriverList;
